import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RotateCcw, Loader2 } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (d) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const PageHistoryDialog = ({ page, open, onOpenChange, onRestored }) => {
  const [versions, setVersions] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!open || !page) return;
    setVersions(null);
    axios.get(`${API}/admin/site-pages/${encodeURIComponent(page.key)}/history`, { headers: authHeaders() })
      .then(r => setVersions(r.data))
      .catch(() => setVersions([]));
  }, [open, page]);

  const restore = async (v) => {
    if (!window.confirm(`Restaurer la version du ${fmt(v.created_at)} ? La version actuelle sera conservée dans l'historique.`)) return;
    setRestoring(v.id);
    try {
      await axios.post(`${API}/admin/versions/restore/${v.id}`, {}, { headers: authHeaders() });
      toast.success('Version restaurée');
      onRestored?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restauration impossible');
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="page-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History size={18} className="text-[#35AFE4]" /> Historique — {page?.name}</DialogTitle>
          <DialogDescription>Chaque enregistrement conserve la version précédente. Vous pouvez revenir en arrière à tout moment.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
          {versions === null ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center" data-testid="page-history-empty">Aucune version enregistrée pour l'instant. Elles apparaîtront après la prochaine modification.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {versions.map((v) => (
                <li key={v.id} className="py-3 flex items-center gap-3" data-testid={`page-version-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{fmt(v.created_at)}</p>
                    <p className="text-xs text-gray-500 truncate">{v.saved_by_name || '—'} · {v.change_summary || 'Modification'}{v.entity_id && page?.kind === 'module' ? ` · ${v.entity_id}` : ''}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(v)} disabled={restoring === v.id} data-testid={`page-version-restore-${v.id}`}>
                    {restoring === v.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                    <span className="ml-1.5">Restaurer</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
